// externals
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { ReadyToSendDialogStatus } from '@makemydeal/dr-dash-types';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as deskingActionTypes from '@makemydeal/dr-dash-store/actionTypes/deskingActionTypes';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';

// components
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';
import { Button } from '@interstate/components/Button';
import { Footer } from './Footer.styles';
import { Grid } from '@interstate/components/Grid';
import ActionButtonInterstate from '../common/ActionButton.interstate';
import { ErrorModalDialogContent } from '../common/ErrorModalDialogContent';
import { Action } from '../constants';
import { useValidationGuard } from '../hooks/useValidationGuard';

export interface IReadyToSendDialogProps {
    show: boolean;
    onNo(): void;
    onHide(): void;
    onSaved(): void;

    dialogHeader: string;
    dialogPrompt?: string;
    dialogPromptHeader?: string;
    action?: number;
    promptSx?: object;
}

const ReadyToSendDialogInterstate = (props: IReadyToSendDialogProps) => {
    const {
        show,
        onNo,
        onSaved,
        onHide,
        dialogHeader,
        dialogPrompt,
        action,
        dialogPromptHeader,
        promptSx = { padding: '0 24px' }
    } = props;

    const [saveOfferStatus, setSaveOfferStatus] = React.useState(ReadyToSendDialogStatus.Ready);
    const [saveOfferHasBeenTriggered, setSaveOfferHasBeenTriggered] = React.useState(false);

    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const searchAndMergeCleanedUp = useSelector(offerInfoSelectors.getSearchAndMergeCleanedUp);
    const currentOfferType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const outsideLienholderData = useSelector(offerReduxSelectors.getOutsideLienholder);
    const hasLienholderData = !!outsideLienholderData.lienHolder;

    const continueInDesking = currentOfferType === CASH && hasLienholderData;

    const dispatch = useDispatch();
    const guard = useValidationGuard();

    const prefix = 'ready-to-send-dialog';

    const handleShowChange = () => {
        if (show) {
            setSaveOfferStatus(ReadyToSendDialogStatus.Ready);
        }
    };

    const handleSaveOfferStatusChange = () => {
        if (!(saveOfferHasBeenTriggered && show)) return;

        if (offerIsBeingSaved === true) {
            return setSaveOfferStatus(ReadyToSendDialogStatus.Sending);
        }

        setSaveOfferHasBeenTriggered(false);

        if (offerHasBeenSaved === true) {
            setSaveOfferStatus(ReadyToSendDialogStatus.Success);
            setTimeout(onSaved, 500);
            return;
        }

        return setSaveOfferStatus(searchAndMergeCleanedUp ? ReadyToSendDialogStatus.Failure_409 : ReadyToSendDialogStatus.Failure);
    };

    const handleYesClick = guard.wrap(() => {
        dispatch(deskingActionCreators.continueInDeskingConfirmClicked());

        const originalAction = Action.CONTINUE_IN_DESKING === action ? deskingActionTypes.CONTINUE_IN_DESKING : undefined;
        if (action === Action.CONTINUE_IN_DESKING && continueInDesking) {
            dispatch(offerReduxActionCreators.setOutsideLienholder({}));
        }
        setSaveOfferHasBeenTriggered(true);
        dispatch(offerActionCreators.saveOffer({ originalAction }));
    });

    React.useEffect(handleShowChange, [show]);
    React.useEffect(handleSaveOfferStatusChange, [offerIsBeingSaved, offerHasBeenSaved, onSaved, show, saveOfferHasBeenTriggered]);

    const isFailure =
        saveOfferStatus === ReadyToSendDialogStatus.Failure || saveOfferStatus === ReadyToSendDialogStatus.Failure_409;

    const footer = (
        <Footer data-testid={`${prefix}-footer`}>
            {!isFailure && (
                <Grid display="flex" justifyContent="end">
                    <Button
                        data-testid={`${prefix}-no-button`}
                        buttonStyle="tertiary"
                        onClick={onNo}
                        disabled={saveOfferStatus === 'sending' || saveOfferStatus === 'success'}
                        sx={{ marginRight: '10px' }}
                    >
                        No
                    </Button>
                    <ActionButtonInterstate
                        data-testid={'ready-to-send-dialog-yes-button'}
                        status={saveOfferStatus}
                        prefix={prefix}
                        successAction="offer-saved"
                        readyPrefix="yes-button"
                        onAction={handleYesClick}
                        actionText="Yes"
                        loadingText="Updating..."
                    />
                </Grid>
            )}
            {isFailure && (
                <Button data-testid={`${prefix}-close`} buttonStyle="secondary" onClick={onNo}>
                    Close
                </Button>
            )}
        </Footer>
    );

    return (
        <Modal
            data-testid={prefix}
            show={show}
            header={
                <Typography data-testid={`${prefix}-header`} variant="h4" color="base.color.blue.900">
                    {dialogHeader}
                </Typography>
            }
            footer={{
                footerComponent: footer
            }}
            onHide={onHide}
            size="small"
        >
            {saveOfferStatus === ReadyToSendDialogStatus.Failure && (
                <ErrorModalDialogContent
                    alertString=" The changes you made to this offer failed to update. Please try again!"
                    contentString="We ran into a problem updating this offer so the information wasn't shared. Please try again."
                />
            )}
            {saveOfferStatus === ReadyToSendDialogStatus.Failure_409 && (
                <ErrorModalDialogContent
                    alertString=" The changes you made to this offer failed to update."
                    contentString="This deal can no longer be updated because its credit application has been attached to another deal. Please continue working from the primary deal."
                    hideSupportInfo
                />
            )}
            {!isFailure && (
                <Typography
                    data-testid={`${prefix}-prompt`}
                    variant="body-md"
                    color="sem.color.on-surface.default"
                    sx={promptSx}
                    tag="div"
                >
                    {dialogPromptHeader && <b>{dialogPromptHeader}</b>}
                    {dialogPrompt}
                </Typography>
            )}
        </Modal>
    );
};

export default ReadyToSendDialogInterstate;
